import { defineStore } from 'pinia'
import { AuthService } from '@lingoking-node-apps/api'
import { UserTypes } from '@/enums/UserTypes.ts'
import { useRequestStateStore } from '@/stores/request-state'
import { useResetStore } from '@/composables/useResetStore'
import router from '@/router'

export type UserProfile = {
  id: string
  email: string
  firstName: string
  lastName: string
  userType: UserTypes
}

type AuthStoreData = {
  loginError: any
  user: UserProfile | null
  returnUrl: string | null
}

const createDefaultStoreData = (): AuthStoreData => {
  return {
    loginError: null,
    user: null,
    returnUrl: null
  }
}

export const useAuthStore = defineStore('auth', {
  persist: true,
  state: (): AuthStoreData => createDefaultStoreData(),
  getters: {
    isPm: (state) => {
      return state.user && state.user.userType == UserTypes.TYPE_PM
    },
    isAdmin: (state) => {
      return state.user && state.user.userType == UserTypes.TYPE_ADMIN
    }
  },
  actions: {
    async login(email: string, password: string) {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        await AuthService.login({ email: email, password: password })

        await this.profile()

        await router.push(this.returnUrl || '/')
      } catch (error: any) {
        requestStateStore.error = error
      } finally {
        requestStateStore.loading = false
      }
    },

    async logout() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        await AuthService.logout()

        this.reset()
      } catch (error: any) {
        requestStateStore.error = error

        this.reset()
      } finally {
        requestStateStore.loading = false
      }
    },

    async refresh() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        const response = await AuthService.refresh()

        return Promise.resolve(response)
      } catch (error: any) {
        requestStateStore.error = error

        this.reset()

        return Promise.reject(error)
      } finally {
        requestStateStore.loading = false
      }
    },

    async profile() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      const response: { user: UserProfile } = await AuthService.profile()

      requestStateStore.loading = false

      if (!response.user) {
        await this.logout()

        return
      }

      this.user = response.user
    },

    reset(options: { redirectToLogin?: boolean } = { redirectToLogin: true }) {
      const resetStore = useResetStore()

      // reset all stores:
      resetStore.all()

      if (options.redirectToLogin) {
        router.push('/login')
      }
    }
  }
})
